/**
 * PDP Core — shared primitives and generic custom elements.
 *
 * Every accessories-style PDP (accessories, xreal-air-vr, xreal-one-vr,
 * rokid…) imports from here so state, base class, utils, and the
 * presentational components (title, price, gallery, product-options,
 * sticky-cart) live in a single source of truth.
 *
 * Template-specific roots live in their own module and import what
 * they need from this core.
 */
import { formatMoney as shopifyFormatMoney } from 'utils';

// ============================================================================
// STATE — global singleton EventTarget store
// ============================================================================

export class PdpState extends EventTarget {
  #state = {
    // Main product
    product: null,
    selectedVariant: null,
    productOptions: [],
    images: [],

    // Frame colour metadata (applies to eyewear products with grouped colours)
    framesByGroup: [],
    frameGroup: null,
    frameTag: '',
    frameColor: '',
    // All frame products (keyed by handle) — used to switch between colours
    // and resolve temple-type compatibility. Populated asynchronously by
    // page roots that need it.
    shopProducts: {},
    // Temple-type filter (e.g. 'cable', 'skul') applied to the frame colour
    // switcher so only compatible frames show when a type is selected.
    activeTemple: null,

    // RX lens product (used on prescription templates)
    rxLensProduct: null,
    rxLensSelectedVariant: null,
    rxLensProductOptions: [],

    // Computed total (main variant + optional rx lens + coating).
    // Page roots maintain this on variant changes so every price-aware
    // component (price, ATC, sticky cart) reads from a single source.
    totalPrice: null,
    totalCompareAtPrice: null,

    // UI
    cartAdding: false,
    isOpenPrescriptionPopup: false,
  };

  get(key) {
    return key.includes('.')
      ? key.split('.').reduce((o, k) => o?.[k], this.#state)
      : this.#state[key];
  }

  set(key, value, silent = false) {
    const oldValue = this.get(key);
    if (oldValue === value) return;

    if (key.includes('.')) {
      const keys = key.split('.');
      const last = keys.pop();
      const target = keys.reduce((o, k) => o[k], this.#state);
      target[last] = value;
    } else {
      this.#state[key] = value;
    }

    if (!silent) {
      this.dispatchEvent(new CustomEvent('state:change', { detail: { key, value, oldValue } }));
      this.dispatchEvent(new CustomEvent(`state:${key}`, { detail: { value, oldValue } }));
    }
  }

  setState(updates, silent = false) {
    Object.entries(updates).forEach(([k, v]) => this.set(k, v, silent));
  }

  getAll() { return { ...this.#state }; }
}

// Single global instance — components find it via `this.state` getter.
if (!window.pdpState) window.pdpState = new PdpState();
export const pdpState = window.pdpState;

// Expose a money formatter to Alpine-backed sections that need to format
// dynamic prices (e.g. `+${pdpFormatMoney(plusPrice)}`) without the shop's
// `money_format` currency code baked in. Defined below after PdpUtils.
Object.defineProperty(window, 'pdpFormatMoney', {
  configurable: true,
  get() { return (cents) => PdpUtils.formatMoneyCompact(cents); },
});

// ============================================================================
// UTILITIES
// ============================================================================

export const PdpUtils = {
  // Uses Intl.NumberFormat with the ACTIVE cart currency (respects
  // Shopify Markets). Falls back to the shop's configured money_format
  // via Shopify's formatMoney helper if Intl isn't available.
  formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const iso = window.theme?.currency_iso || 'USD';
    try {
      return new Intl.NumberFormat(
        navigator.language || 'en',
        { style: 'currency', currency: iso, currencyDisplay: 'narrowSymbol' }
      ).format(cents / 100);
    } catch {
      const fallback =
        window.theme?.money_product_price_format ||
        window.theme?.money_format ||
        '${{amount}}';
      return shopifyFormatMoney(cents, fallback);
    }
  },

  // Compact variant for inline badges (e.g. +€43 instead of +€43,00).
  // Drops fractional digits when the amount has no cents.
  formatMoneyCompact(cents) {
    if (cents == null || isNaN(cents)) return '';
    const iso = window.theme?.currency_iso || 'USD';
    const isWhole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(
        navigator.language || 'en',
        {
          style: 'currency',
          currency: iso,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: isWhole ? 0 : 2,
          maximumFractionDigits: isWhole ? 0 : 2,
        }
      ).format(cents / 100);
    } catch {
      let out = PdpUtils.formatMoney(cents);
      if (isWhole) out = out.replace(/([.,])00(?!\d)/, '');
      return out;
    }
  },

  escapeHtml(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  },

  getFrameTagByProduct(product) {
    return product?.tags?.find((t) => typeof t === 'string' && t.includes('frame_')) || '';
  },

  getFrameColorByProduct(product) {
    const tag = PdpUtils.getFrameTagByProduct(product);
    return tag ? tag.split('~')[1] || '' : '';
  },

  getFrameGroupByProduct(product) {
    const tag = PdpUtils.getFrameTagByProduct(product);
    if (!tag) return null;
    const left = tag.split('~')[0];
    const parts = left.split('_');
    return parts[1] || null;
  },

  // Extract temple-type tag suffix from a product's tags
  // (e.g. 'temple-type_groupname~cable' → 'cable').
  getTempleTypeByProduct(product) {
    const tag = (product?.tags || []).find((t) => typeof t === 'string' && t.startsWith('temple-type_'));
    if (!tag) return null;
    return tag.split('~')[1] || null;
  },

  // Does this product's tags include a `temple-type_{group}~{type}` pair?
  productMatchesTempleType(product, templeType) {
    if (!templeType) return true;
    const group = PdpUtils.getFrameGroupByProduct(product);
    if (!group) return false;
    return (product.tags || []).includes(`temple-type_${group}~${templeType}`);
  },

  findVariantByOptions(product, selectedOptions) {
    return product.variants.find((v) =>
      (v.options || []).every((val, i) => val === selectedOptions[i])
    );
  },

  buildImages(product) {
    return (product?.media || [])
      .filter((m) => m.media_type === undefined || m.media_type === 'image')
      .map((m, i) => ({
        id: m.id ?? i,
        src: m.preview_image?.src || m.src,
        alt: m.alt || '',
      }));
  },
};

// ============================================================================
// BASE COMPONENT — auto-aborts listeners on disconnect.
// ============================================================================

export class PdpBaseComponent extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.setupListeners();
    this.init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.cleanup();
  }

  get signal() { return this.#abortController?.signal; }
  get state() { return window.pdpState; }

  init() {}
  setupListeners() {}
  cleanup() {}

  listen(target, event, handler, options = {}) {
    target.addEventListener(event, handler, { signal: this.signal, ...options });
  }

  onStateChange(key, handler) {
    this.listen(this.state, `state:${key}`, (e) => handler(e.detail));
  }

  emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
  }
}

// ============================================================================
// SHARED CUSTOM ELEMENTS
// ============================================================================

class PdpTitle extends PdpBaseComponent {
  init() { this.render(); }
  setupListeners() {
    this.onStateChange('product', () => this.render());
    this.onStateChange('frameGroup', () => this.render());
  }
  render() {
    const el = this.querySelector('[data-pdp-title-text]');
    if (!el) return;
    const group = this.state.get('frameGroup');
    const product = this.state.get('product');
    el.textContent = group || product?.title || '';
  }
}

class PdpPrice extends PdpBaseComponent {
  init() { this.render(); }
  setupListeners() {
    this.onStateChange('selectedVariant', () => this.render());
    this.onStateChange('totalPrice', () => this.render());
    this.onStateChange('totalCompareAtPrice', () => this.render());
  }
  render() {
    const variant = this.state.get('selectedVariant');
    if (!variant) return;

    const totalPrice = this.state.get('totalPrice');
    const totalCompare = this.state.get('totalCompareAtPrice');
    const price = totalPrice != null ? totalPrice : variant.price;
    const compareValue = totalCompare != null ? totalCompare : variant.compare_at_price;

    const current = this.querySelector('[data-pdp-price-current]');
    const compare = this.querySelector('[data-pdp-price-compare]');
    if (current) current.textContent = PdpUtils.formatMoney(price);
    if (compare) {
      const showCompare = compareValue && compareValue > price;
      compare.textContent = showCompare ? PdpUtils.formatMoney(compareValue) : '';
      compare.toggleAttribute('hidden', !showCompare);
    }
  }
}

/**
 * <pdp-add-to-cart> — just a button wrapper. Fires `pdp:add-to-cart` and
 * reflects availability/busy state. Each page root decides what to do
 * with the event (add directly / open prescription popup).
 */
class PdpAddToCart extends PdpBaseComponent {
  init() { this.render(); }
  setupListeners() {
    this.onStateChange('selectedVariant', () => this.render());
    this.onStateChange('totalPrice', () => this.render());
    this.onStateChange('totalCompareAtPrice', () => this.render());
    this.onStateChange('cartAdding', () => this.render());

    const button = this.querySelector('[data-pdp-atc-button]');
    if (button) this.listen(button, 'click', () => this.emit('pdp:add-to-cart'));
  }
  render() {
    const variant = this.state.get('selectedVariant');
    const adding = this.state.get('cartAdding');
    const button = this.querySelector('[data-pdp-atc-button]');
    const label = this.querySelector('[data-pdp-atc-label]');
    const priceWrap = this.querySelector('[data-pdp-atc-price]');
    const priceCurrent = this.querySelector('[data-pdp-atc-price-current]');
    const priceCompare = this.querySelector('[data-pdp-atc-price-compare]');
    if (!button || !variant) return;

    const totalPrice = this.state.get('totalPrice');
    const totalCompare = this.state.get('totalCompareAtPrice');
    const price = totalPrice != null ? totalPrice : variant.price;
    const compareValue = totalCompare != null ? totalCompare : variant.compare_at_price;

    const available = !!variant.available;
    button.disabled = !available || adding;
    button.setAttribute('aria-busy', adding ? 'true' : 'false');
    if (label) {
      label.textContent = !available ? 'Sold out' : adding ? 'Adding…' : 'Add to cart';
    }
    if (priceCurrent) priceCurrent.textContent = PdpUtils.formatMoney(price);
    if (priceCompare) {
      const showCompare = compareValue && compareValue > price;
      priceCompare.textContent = showCompare ? PdpUtils.formatMoney(compareValue) : '';
      priceCompare.toggleAttribute('hidden', !showCompare);
    }
    if (priceWrap) priceWrap.toggleAttribute('hidden', !available);
  }
}

class PdpProductOptions extends PdpBaseComponent {
  #descriptions = {};

  init() {
    const descEl = this.querySelector('[data-pdp-option-descriptions]');
    if (descEl) {
      try { this.#descriptions = JSON.parse(descEl.textContent); } catch { /* noop */ }
    }
    this.render();
  }
  setupListeners() {
    this.onStateChange('productOptions', () => this.render());
    this.onStateChange('selectedVariant', () => this.render());
    this.onStateChange('product', () => this.render());

    this.listen(this, 'click', (e) => {
      const btn = e.target.closest('[data-pdp-option-value]');
      if (!btn) return;
      this.emit('pdp:change-variant', {
        optionName: btn.dataset.pdpOptionName,
        value: btn.dataset.pdpOptionValue,
      });
    });
  }
  render() {
    const root = this.querySelector('[data-pdp-options-root]');
    const product = this.state.get('product');
    const options = this.state.get('productOptions');
    const variant = this.state.get('selectedVariant');
    if (!root || !product || !variant) return;

    if (product.has_only_default_variant) {
      root.innerHTML = '';
      this.toggleAttribute('hidden', true);
      return;
    }
    this.toggleAttribute('hidden', false);

    const esc = PdpUtils.escapeHtml;
    const descs = this.#descriptions;

    root.innerHTML = options
      .filter((o) => !['Tint', 'Color'].includes(o.name))
      .map((option) => {
        const selectedValue = variant.options[option.position - 1];
        const descKey = `${option.name}::${selectedValue}`;
        const desc = descs[descKey];

        const buttons = option.values.map((value) => {
          const isActive = value === selectedValue;
          const safeValue = esc(value);
          return `
            <button
              type="button"
              class="pdp-tag${isActive ? ' pdp-tag--active' : ''}"
              data-pdp-option-name="${esc(option.name)}"
              data-pdp-option-value="${safeValue}"
            ><span class="pdp-tag__value">${safeValue.replace('(', '<br>(')}</span></button>
          `;
        }).join('');

        return `
          <div class="pdp-option-group">
            <div class="pdp-option-group__label">
              <span class="pdp-option-group__label-text">${esc(option.name)}</span>
              <span class="pdp-option-group__label-value">${esc(selectedValue || '')}</span>
            </div>
            <div class="pdp-option-group__grid">${buttons}</div>
            ${desc ? `<p class="pdp-option-group__desc">${esc(desc)}</p>` : ''}
          </div>
        `;
      }).join('');
  }
}

class PdpFrameColorSwitcher extends PdpBaseComponent {
  init() { this.render(); }
  setupListeners() {
    this.onStateChange('framesByGroup', () => this.render());
    this.onStateChange('frameGroup', () => this.render());
    this.onStateChange('frameTag', () => this.render());
    this.onStateChange('frameColor', () => this.render());
    this.onStateChange('activeTemple', () => this.render());

    this.listen(this, 'click', (e) => {
      const btn = e.target.closest('[data-pdp-frame-index]');
      if (!btn) return;
      const idx = parseInt(btn.dataset.pdpFrameIndex, 10);
      const product = this.#visibleFrames()[idx];
      if (product) this.emit('pdp:change-frame-product', { product });
    });
  }
  #visibleFrames() {
    const framesByGroup = this.state.get('framesByGroup');
    const frameGroup = this.state.get('frameGroup');
    const activeTemple = this.state.get('activeTemple');
    const group = framesByGroup.find((g) => g.frameGroup === frameGroup);
    const all = group?.allFrames || [];
    if (!activeTemple) return all;
    return all.filter((p) => PdpUtils.productMatchesTempleType(p, activeTemple));
  }
  render() {
    const frameTag = this.state.get('frameTag');
    const frameColor = this.state.get('frameColor');
    const allFrames = this.#visibleFrames();

    if (allFrames.length === 0) {
      this.toggleAttribute('hidden', true);
      return;
    }
    this.toggleAttribute('hidden', false);

    const nameEl = this.querySelector('[data-pdp-frame-color-name]');
    if (nameEl) nameEl.textContent = frameColor || '';

    const list = this.querySelector('[data-pdp-frame-color-list]');
    if (list) {
      const esc = PdpUtils.escapeHtml;
      list.innerHTML = allFrames.map((p, i) => {
        const tag = PdpUtils.getFrameTagByProduct(p);
        const color = PdpUtils.getFrameColorByProduct(p);
        const active = tag === frameTag;
        return `
          <button
            type="button"
            class="pdp-frame-swatch${active ? ' pdp-frame-swatch--active' : ''}"
            data-pdp-frame-index="${i}"
            aria-label="Change frame color to ${esc(color)}"
            aria-pressed="${active}"
          >
            <span class="pdp-frame-swatch__chip" data-color-swatch="${esc(color)}"></span>
          </button>
        `;
      }).join('');
    }

    const descEl = this.querySelector('[data-pdp-frame-color-description]');
    if (descEl) {
      const swatchMeta = window.colorSwatch?.[frameTag];
      const hasDesc = !!swatchMeta?.description;
      descEl.innerHTML = hasDesc ? swatchMeta.description : '';
      descEl.toggleAttribute('hidden', !hasDesc);
    }
  }
}

class PdpGallery extends PdpBaseComponent {
  init() { this.render(); }
  setupListeners() {
    this.onStateChange('images', () => this.render());
  }
  render() {
    const images = this.state.get('images') || [];
    if (images.length === 0) { this.innerHTML = ''; return; }
    const esc = PdpUtils.escapeHtml;

    const mobileSlides = images.map((img) => `
      <div class="pdp-gallery__slide">
        <img src="${esc(img.src)}" alt="${esc(img.alt)}" loading="lazy">
      </div>
    `).join('');

    const mobileDots = images.map((_, i) => `
      <span class="pdp-gallery__dot${i === 0 ? ' pdp-gallery__dot--active' : ''}" data-pdp-dot-index="${i}"></span>
    `).join('');

    const desktopItems = images.map((img, i) => `
      <div class="pdp-gallery__grid-item${i === 0 ? ' pdp-gallery__grid-item--featured' : ''}">
        <img src="${esc(img.src)}" alt="${esc(img.alt)}" loading="${i < 2 ? 'eager' : 'lazy'}">
      </div>
    `).join('');

    this.innerHTML = `
      <div class="pdp-gallery__mobile">
        <div class="pdp-gallery__track" data-pdp-gallery-track>${mobileSlides}</div>
        ${images.length > 1 ? `<div class="pdp-gallery__dots" data-pdp-gallery-dots>${mobileDots}</div>` : ''}
      </div>
      <div class="pdp-gallery__desktop">${desktopItems}</div>
    `;

    this.#wireMobileTracker();
  }
  #wireMobileTracker() {
    const track = this.querySelector('[data-pdp-gallery-track]');
    const dots = this.querySelectorAll('[data-pdp-dot-index]');
    if (!track || dots.length === 0) return;

    const update = () => {
      const slideWidth = track.clientWidth;
      const idx = Math.round(track.scrollLeft / slideWidth);
      dots.forEach((d, i) => {
        d.classList.toggle('pdp-gallery__dot--active', i === idx);
      });
    };
    this.listen(track, 'scroll', update, { passive: true });
  }
}

class PdpExternalGallery extends PdpBaseComponent {
  init() { this.render(); }
  setupListeners() {
    this.onStateChange('product', () => this.render());
  }
  render() {
    const product = this.state.get('product');
    const images = product?.metafields?.external_gallery || [];
    if (!Array.isArray(images) || images.length === 0) {
      this.innerHTML = '';
      this.toggleAttribute('hidden', true);
      return;
    }
    this.toggleAttribute('hidden', false);
    const esc = PdpUtils.escapeHtml;
    this.innerHTML = images.map((src) => `
      <div class="pdp-external-gallery__item">
        <img src="${esc(src)}" loading="lazy" alt="">
      </div>
    `).join('');
  }
}

class PdpStickyCart extends PdpBaseComponent {
  init() {
    this.render();
    this.#updateVisibility();
  }
  setupListeners() {
    this.onStateChange('selectedVariant', () => this.render());
    this.onStateChange('totalPrice', () => this.render());
    this.onStateChange('totalCompareAtPrice', () => this.render());
    this.onStateChange('cartAdding', () => this.render());
    this.onStateChange('frameGroup', () => this.render());
    this.onStateChange('product', () => this.render());

    const button = this.querySelector('[data-pdp-atc-button]');
    if (button) this.listen(button, 'click', () => this.emit('pdp:add-to-cart'));

    this.listen(window, 'scroll', () => this.#updateVisibility(), { passive: true });
    this.listen(window, 'resize', () => this.#updateVisibility(), { passive: true });
  }
  #updateVisibility() {
    const primaryAtc = document.querySelector('pdp-add-to-cart');
    if (!primaryAtc) { this.dataset.hidden = 'false'; return; }
    const rect = primaryAtc.getBoundingClientRect();
    const primaryVisible = rect.bottom > 0 && rect.top < window.innerHeight;
    this.dataset.hidden = primaryVisible ? 'true' : 'false';
  }
  render() {
    const variant = this.state.get('selectedVariant');
    const product = this.state.get('product');
    const group = this.state.get('frameGroup');
    const adding = this.state.get('cartAdding');
    if (!variant) return;

    const totalPrice = this.state.get('totalPrice');
    const totalCompare = this.state.get('totalCompareAtPrice');
    const priceValue = totalPrice != null ? totalPrice : variant.price;
    const compareValue = totalCompare != null ? totalCompare : variant.compare_at_price;

    const title = this.querySelector('[data-pdp-sticky-title]');
    const price = this.querySelector('[data-pdp-sticky-price]');
    const compare = this.querySelector('[data-pdp-sticky-compare]');
    const button = this.querySelector('[data-pdp-atc-button]');
    const label = this.querySelector('[data-pdp-atc-label]');

    if (title) title.textContent = group || product?.title || '';
    if (price) price.textContent = PdpUtils.formatMoney(priceValue);
    if (compare) {
      const showCompare = compareValue && compareValue > priceValue;
      compare.textContent = showCompare ? PdpUtils.formatMoney(compareValue) : '';
      compare.toggleAttribute('hidden', !showCompare);
    }
    if (button) {
      button.disabled = !variant.available || adding;
      button.setAttribute('aria-busy', adding ? 'true' : 'false');
    }
    if (label) {
      label.textContent = !variant.available ? 'Sold out' : adding ? 'Adding…' : 'Add to cart';
    }
  }
}

/**
 * <pdp-collapsible-text> — mobile-only 3-line clamp with a show-more toggle.
 * Works with any rich-text content inside [data-pdp-collapsible-body].
 * Desktop always shows the full content; the toggle button hides itself
 * when content doesn't actually overflow.
 */
class PdpCollapsibleText extends PdpBaseComponent {
  init() {
    this.body = this.querySelector('[data-pdp-collapsible-body]');
    this.toggle = this.querySelector('[data-pdp-collapsible-toggle]');
    this.#syncToggleVisibility();
    // Re-check on resize — a wider viewport may fit the text without clamping.
    this.listen(window, 'resize', () => this.#syncToggleVisibility(), { passive: true });
  }
  setupListeners() {
    this.listen(this, 'click', (e) => {
      const t = e.target.closest('[data-pdp-collapsible-toggle]');
      if (!t) return;
      const expanded = !this.hasAttribute('data-expanded');
      this.toggleAttribute('data-expanded', expanded);
      t.textContent = expanded
        ? (t.dataset.labelLess || 'Show less')
        : (t.dataset.labelMore || 'Show more');
    });
  }
  #syncToggleVisibility() {
    if (!this.body || !this.toggle) return;
    // Only overflow-check when clamp is active (mobile). On wide screens the
    // CSS media query removes the clamp and the toggle stays hidden.
    const isMobile = window.matchMedia('(max-width: 1023px)').matches;
    if (!isMobile) {
      this.toggle.hidden = true;
      return;
    }
    // Compare natural height vs clamped height by briefly reading scrollHeight.
    const clampedHeight = this.body.clientHeight;
    // Temporarily unclamp to measure
    const prevExpanded = this.hasAttribute('data-expanded');
    this.setAttribute('data-expanded', '');
    const naturalHeight = this.body.scrollHeight;
    if (!prevExpanded) this.removeAttribute('data-expanded');
    this.toggle.hidden = naturalHeight <= clampedHeight + 2;
  }
}

/**
 * <pdp-lens-options> — variant selector for an RX lens product with
 * +plus_price badges from the product's `options_extension` metafield.
 * Shared because every rx-style template (xreal, mask-optics, safety…)
 * uses it.
 */
class PdpLensOptions extends PdpBaseComponent {
  init() { this.render(); }

  setupListeners() {
    this.onStateChange('rxLensProduct', () => this.render());
    this.onStateChange('rxLensProductOptions', () => this.render());
    this.onStateChange('rxLensSelectedVariant', () => this.render());

    this.listen(this, 'click', (e) => {
      const btn = e.target.closest('[data-pdp-lens-option-value]');
      if (!btn) return;
      this.emit('pdp:change-lens-variant', {
        optionName: btn.dataset.pdpLensOptionName,
        value: btn.dataset.pdpLensOptionValue,
      });
    });
  }

  render() {
    const root = this.querySelector('[data-pdp-lens-options-root]');
    const lensProduct = this.state.get('rxLensProduct');
    const options = this.state.get('rxLensProductOptions') || [];
    const variant = this.state.get('rxLensSelectedVariant');
    if (!root || !lensProduct || !variant) return;

    const esc = PdpUtils.escapeHtml;
    const ext = lensProduct.metafields?.options_extension || {};

    // Skip "Color" — handled by a dedicated swatches block where applicable.
    const visibleOptions = options.filter((o) => o.name !== 'Color');

    root.innerHTML = visibleOptions.map((option) => {
      const selectedValue = variant.options[option.position - 1];
      const optExt = ext[option.name] || {};

      // Compute plus-price dynamically from the real variant prices so it
      // matches the customer's currency (Markets auto-converts variant.price
      // but NOT metafield numbers). Baseline = first value's variant, holding
      // the other options at their current values.
      const baselineOptions = [...variant.options];
      baselineOptions[option.position - 1] = option.values[0];
      const baselineVariant = PdpUtils.findVariantByOptions(lensProduct, baselineOptions);
      const basePrice = baselineVariant?.price ?? 0;

      const buttons = option.values.map((value) => {
        const isActive = value === selectedValue;
        const trialOptions = [...variant.options];
        trialOptions[option.position - 1] = value;
        const trialVariant = PdpUtils.findVariantByOptions(lensProduct, trialOptions);
        const plusPrice = trialVariant ? trialVariant.price - basePrice : 0;

        const safeValue = esc(value).replace(/\s*\(/g, '<br>(');
        const plusSegment = plusPrice > 0
          ? `<span class="pdp-tag__plus">+${PdpUtils.formatMoneyCompact(plusPrice)}</span>`
          : '';
        return `
          <button
            type="button"
            class="pdp-tag${isActive ? ' pdp-tag--active' : ''}"
            data-pdp-lens-option-name="${esc(option.name)}"
            data-pdp-lens-option-value="${esc(value)}"
          ><span class="pdp-tag__value">${safeValue}</span>${plusSegment}</button>
        `;
      }).join('');

      const desc = optExt[selectedValue]?.description;

      return `
        <div class="pdp-option-group">
          <div class="pdp-option-group__label">
            <span class="pdp-option-group__label-text">${esc(option.name)}</span>
            <span class="pdp-option-group__label-value">${esc(selectedValue || '')}</span>
          </div>
          <div class="pdp-option-group__grid">${buttons}</div>
          ${desc ? `<div class="pdp-option-group__desc">${desc}</div>` : ''}
        </div>
      `;
    }).join('');
  }
}

// ============================================================================
// REGISTER SHARED ELEMENTS (guarded — modules load once)
// ============================================================================

const sharedComponents = {
  'pdp-title': PdpTitle,
  'pdp-price': PdpPrice,
  'pdp-add-to-cart': PdpAddToCart,
  'pdp-product-options': PdpProductOptions,
  'pdp-lens-options': PdpLensOptions,
  'pdp-frame-color-switcher': PdpFrameColorSwitcher,
  'pdp-gallery': PdpGallery,
  'pdp-external-gallery': PdpExternalGallery,
  'pdp-sticky-cart': PdpStickyCart,
  'pdp-collapsible-text': PdpCollapsibleText,
};

Object.entries(sharedComponents).forEach(([name, ctor]) => {
  if (!customElements.get(name)) customElements.define(name, ctor);
});
